import React from 'react';
import { useRefReady, usePreviousValue } from 'hellospa/common/hooks';
import { signatureRequestContext, fieldsContext, zoomContext } from './context';
import DocumentTargetWrap from './document-target-wrap';
import Page from './document/page';
import styles from './document.scss';

function useCenterDocument(ref) {
  const [centered, setCentered] = React.useState(0);
  React.useEffect(() => {
    if (centered !== true) {
      if (ref.current != null && ref.current.children.length > 0) {
        const left = Math.round((ref.current.scrollWidth - ref.current.clientWidth) / 2);
        ref.current.scrollLeft = left;

        // I still don't know why, but sometimes I can't set scrollLeft, so this
        // will have to try again in another update.
        if (ref.current.scrollLeft === left) {
          return setCentered(true);
        }
      }

      // Force an update by incrementing the counter
      if (centered < 10) {
        setCentered(centered + 1);
      }
    }
  }, [centered, ref]);
}

function useUpdateOnResize(context) {
  const { pageContainerRef, setPageContainerRef } = context;
  const prevSize = usePreviousValue(
    pageContainerRef.current && pageContainerRef.current.getBoundingClientRect(),
  );

  React.useLayoutEffect(() => {
    if (pageContainerRef.current) {
      const bbox = pageContainerRef.current.getBoundingClientRect();

      if (
        !prevSize
        || prevSize.width !== bbox.width
        || prevSize.height !== bbox.height
      ) {
        setPageContainerRef(pageContainerRef.current);
      }
    }
  }, [prevSize, pageContainerRef, setPageContainerRef]);
}

// This needs to be defined here so it doesn't change on each render.
const NOOP = (Page) => Page;

function Document({
  className,
  noBackground,
  children = null,
  showImg = true,
  onPageClick,
  wrapPage = NOOP,
  showUneditableMessage = false,
  uneditableDocumentIds,
}) {
  const WrappedPage = React.useMemo(
    () => wrapPage(Page),
    [wrapPage],
  );
  const context = React.useContext(signatureRequestContext);
  const fields = React.useContext(fieldsContext);
  const { zoom, pxMaxPageWidth, fitWidthScale } = React.useContext(zoomContext);
  useCenterDocument(context.pageContainerRef);
  useUpdateOnResize(context);
  // Page positions depend on the size of pageContainerRef, so they can't render
  // until it's in place and fitWidthScale has been calculated for this container.
  const readyForPages = useRefReady(context.pageContainerRef) && (
    fitWidthScale > 0
    // since JSDOM doesn't do layout fitWidthScale doesn't get calculated in tests.
    || navigator.userAgent.includes('jsdom')
  );
  const fieldsByPage = React.useMemo(() => {
    const fieldsByPage = context.pages.map(() => []);
    fields.forEach(
      (f) => fieldsByPage[f.pageIndex].push(f),
    );
    return fieldsByPage;
  }, [fields, context.pages]);
  const pagePositions = React.useMemo(() => {
    const maxWidth = Math.max(...context.pages.map((p) => p.width));

    return context.pages.map((page, pageIndex) => {
      const widthPercent = page.width / maxWidth * 100;

      let margin = '0';
      if (context.topGap) {
        // with `display: flex` margins dont' overlap, so the top and bottom can
        // have the same margin on all pages.
        margin = `${context.pageGap}px 0`;
      } else if (pageIndex > 0) {
        // Fall back to Editor < 2.3 behavior of only applying a top margin
        margin = `${context.pageGap}px 0 0 0`;
      }

      return {
        width: `${widthPercent}%`,
        margin,
      };
    });
  }, [context.pageGap, context.pages, context.topGap]);
  return (
    <DocumentTargetWrap className={className} noBackground={noBackground}>
      <div
        className={styles.pageContainer}
        style={{ width: pxMaxPageWidth * zoom }}
      >
        { readyForPages && context.pages.map((page, pageIndex) => (
          <WrappedPage
            key={`page${pageIndex}-${page.src}`}
            onPageClick={onPageClick}
            {...pagePositions[pageIndex]}
            showImg={showImg}
            pageIndex={pageIndex}
            page={page}
            fields={fieldsByPage[pageIndex]}
            showUneditableMessage={
              showUneditableMessage && (uneditableDocumentIds.has(page.documentId))
            } />
        )) }
      </div>
      {children}
    </DocumentTargetWrap>
  );
}

export default Document;
